import i18n from '../i18n/i18n';

export class DynamicTranslator {
  private translationCache: Record<string, Record<string, string>> = {};
  private observer: MutationObserver | null = null;

  constructor() {
    this.loadTranslations();
  }

  // localStorage에서 번역 데이터 로드
  private loadTranslations() {
    const saved = localStorage.getItem("translations");
    if (saved) {
      try {
        this.translationCache = JSON.parse(saved);
      } catch (error) {
        console.error('Failed to load translations:', error);
      }
    }
  }

  // 텍스트 번역
  private translateText(text: string, targetLang: string): string {
    const cleanText = text.trim();
    if (!cleanText || !this.translationCache[targetLang]) {
      return text;
    }

    // 정확히 일치하는 번역 찾기
    if (this.translationCache[targetLang][cleanText]) {
      return this.translationCache[targetLang][cleanText];
    }

    // 짧은 텍스트(50자 이하)만 부분 일치 허용
    if (cleanText.length <= 50) {
      // 키가 현재 텍스트를 포함하는 경우만 (역방향은 제외)
      for (const [key, value] of Object.entries(this.translationCache[targetLang])) {
        if (key === cleanText) {
          return value;
        }
      }
      
      // 정확한 단어 경계로 매칭 (부분 문자열이 아닌 완전한 단어)
      for (const [key, value] of Object.entries(this.translationCache[targetLang])) {
        if (key.length <= 50 && this.isExactWordMatch(cleanText, key)) {
          return value;
        }
      }
    }

    return text;
  }

  // 정확한 단어 매칭 확인
  private isExactWordMatch(text: string, key: string): boolean {
    // 완전히 동일한 경우
    if (text === key) return true;
    
    // 길이 차이가 너무 큰 경우 제외
    if (Math.abs(text.length - key.length) > 5) return false;
    
    // 특수문자나 공백을 제거하고 비교
    const normalizeText = (str: string) => str.replace(/[^\w가-힣]/g, '').toLowerCase();
    return normalizeText(text) === normalizeText(key);
  }

  // DOM 요소의 텍스트 번역
  private translateElement(element: Element, targetLang: string) {
    // 텍스트 노드만 처리
    const walker = document.createTreeWalker(
      element,
      NodeFilter.SHOW_TEXT,
      {
        acceptNode: (node) => {
          // 스크립트, 스타일 태그 내부는 제외
          const parent = node.parentElement;
          if (parent && (parent.tagName === 'SCRIPT' || parent.tagName === 'STYLE')) {
            return NodeFilter.FILTER_REJECT;
          }
          // 빈 텍스트나 공백만 있는 텍스트는 제외
          if (!node.textContent?.trim()) {
            return NodeFilter.FILTER_REJECT;
          }
          return NodeFilter.FILTER_ACCEPT;
        }
      }
    );

    const textNodes: Text[] = [];
    let node;
    while (node = walker.nextNode()) {
      textNodes.push(node as Text);
    }

    // 각 텍스트 노드 번역
    textNodes.forEach(textNode => {
      const originalText = textNode.textContent || '';
      const translatedText = this.translateText(originalText, targetLang);
      
      if (translatedText !== originalText) {
        textNode.textContent = translatedText;
      }
    });
  }

  // 페이지 전체 번역
  public translatePage(targetElement?: Element) {
    const currentLang = i18n.language;
    const baseLang = currentLang.replace(/-mobile$/, '');
    
    if (baseLang === 'ko') {
      return; // 한국어는 번역하지 않음
    }

    console.log(`[DynamicTranslator] Translating to: ${currentLang}`);
    const element = targetElement || document.body;
    this.translateElement(element, currentLang);
  }

  // 디버깅용 번역 로그
  public debugTranslation(text: string, targetLang: string = i18n.language): void {
    const result = this.translateText(text, targetLang);
    console.log(`[Translation Debug] "${text}" -> "${result}" (${targetLang})`);
    
    // 사용 가능한 번역 키들 출력
    if (this.translationCache[targetLang]) {
      const availableKeys = Object.keys(this.translationCache[targetLang])
        .filter(key => key.includes(text) || text.includes(key))
        .slice(0, 5);
      console.log(`[Available Keys]`, availableKeys);
    }
  }

  // DOM 변화 감지하여 자동 번역
  public startAutoTranslation(targetElement?: Element) {
    const element = targetElement || document.body;
    
    // 초기 번역
    this.translatePage(element);

    // MutationObserver로 DOM 변화 감지
    this.observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              this.translateElement(node as Element, i18n.language);
            }
          });
        }
      });
    });

    this.observer.observe(element, {
      childList: true,
      subtree: true
    });
  }

  // 자동 번역 중지
  public stopAutoTranslation() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  }

  // 번역 캐시 업데이트
  public updateTranslations() {
    this.loadTranslations();
  }
}

// 싱글톤 인스턴스
export const dynamicTranslator = new DynamicTranslator();